import { ComponentType } from 'react';
import { Settings } from 'lucide-react';

import Github from '@/assets/ico/vendor/github.svg?c';
import Gitlab from '@/assets/ico/vendor/gitlab.svg?c';
import Bitbucket from '@/assets/ico/vendor/bitbucket.svg?c';
import Gitea from '@/assets/ico/vendor/gitea.svg?c';
import Azure from '@/assets/ico/vendor/azure.svg?c';

import { FormControl } from '@@/form-components/FormControl';
import { FormSection } from '@@/form-components/FormSection';
import { Icon } from '@@/Icon';
import { Input } from '@@/form-components/Input';
import { BoxSelector, BoxSelectorOption } from '@@/BoxSelector';
import { RadioGroup } from '@@/RadioGroup/RadioGroup';

import { AuthTypeOption, Provider } from '../types';

type SVGComponent = ComponentType<React.SVGProps<SVGSVGElement>>;

export type ProviderConfig = {
  icon: ComponentType | SVGComponent;
  title: string;
  description: string;
  fixedAuthType?: AuthTypeOption;
  usernameTooltip?: string;
  passwordLabel: string;
  passwordTooltip?: string;
};

export const providerConfigs: Record<Provider, ProviderConfig> = {
  [Provider.GitHub]: {
    icon: Github,
    title: 'GitHub',
    description:
      'Authenticate with a Personal Access Token. The token is used as the password in Basic Auth, this is how GitHub expects it.',
    fixedAuthType: AuthTypeOption.Basic,
    usernameTooltip: 'Your GitHub username',
    passwordLabel: 'Personal Access Token',
    passwordTooltip: 'A fine-grained or classic PAT with repo access.',
  },
  [Provider.GitLab]: {
    icon: Gitlab,
    title: 'GitLab',
    description:
      'Authenticate with a Personal Access Token. GitLab uses the token as the password in Basic Auth.',
    fixedAuthType: AuthTypeOption.Basic,
    usernameTooltip: 'Your GitLab username',
    passwordLabel: 'Personal Access Token',
    passwordTooltip: 'A PAT with read_repository or api scope.',
  },
  [Provider.BitbucketCloud]: {
    icon: Bitbucket,
    title: 'Bitbucket Cloud',
    description:
      'Authenticate with an App Password. Bitbucket Cloud uses App Passwords for Basic Auth.',
    fixedAuthType: AuthTypeOption.Basic,
    usernameTooltip: 'Your Bitbucket username',
    passwordLabel: 'App Password',
    passwordTooltip: 'A Bitbucket App Password with repository read access.',
  },
  [Provider.BitbucketDataCenter]: {
    icon: Bitbucket,
    title: 'Bitbucket Data Center',
    description:
      'Authenticate with a Personal Access Token. Bitbucket Data Center uses the token in Bearer Token Auth.',
    fixedAuthType: AuthTypeOption.Token,
    usernameTooltip: 'Your Bitbucket username',
    passwordLabel: 'Personal Access Token',
    passwordTooltip: 'A PAT with repository read access.',
  },
  [Provider.AzureDevOps]: {
    icon: Azure,
    title: 'Azure DevOps',
    description:
      'Authenticate with a Personal Access Token. Azure DevOps uses Basic Authentication.',
    fixedAuthType: AuthTypeOption.Basic,
    usernameTooltip: 'Your Azure DevOps username',
    passwordLabel: 'Personal Access Token',
    passwordTooltip: 'A PAT with Code (Read) permission.',
  },
  [Provider.GiteaForgejo]: {
    icon: Gitea,
    title: 'Gitea / Forgejo',
    description: 'Authenticate with an Access Token for Gitea or Forgejo.',
    fixedAuthType: AuthTypeOption.Token,
    usernameTooltip: 'Your Gitea/Forgejo username',
    passwordLabel: 'Access Token',
    passwordTooltip: 'An API access token with repository read access.',
  },
  [Provider.Custom]: {
    icon: Settings,
    title: 'Custom',
    description:
      'Configure authentication manually for any Git provider. Choose Basic or Token auth.',
    usernameTooltip: 'Your Git username',
    passwordLabel: 'Password',
  },
};

const authTypeOptions = [
  { value: AuthTypeOption.Basic, label: 'Basic' },
  { value: AuthTypeOption.Token, label: 'Token' },
] as const;

export const providerOptions: BoxSelectorOption<Provider>[] = [
  {
    id: 'github',
    icon: Github,
    iconType: 'logo',
    label: 'GitHub',
    value: Provider.GitHub,
  },
  {
    id: 'gitlab',
    icon: Gitlab,
    iconType: 'logo',
    label: 'GitLab',
    value: Provider.GitLab,
  },
  {
    id: 'bitbucket-cloud',
    icon: Bitbucket,
    iconType: 'logo',
    label: 'Bitbucket Cloud',
    value: Provider.BitbucketCloud,
  },
  {
    id: 'bitbucket-data-center',
    icon: Bitbucket,
    iconType: 'logo',
    label: 'Bitbucket Data Center',
    value: Provider.BitbucketDataCenter,
  },
  {
    id: 'azure-devops',
    icon: Azure,
    iconType: 'logo',
    label: 'Azure DevOps',
    value: Provider.AzureDevOps,
  },
  {
    id: 'gitea-forgejo',
    icon: Gitea,
    iconType: 'logo',
    label: 'Gitea / Forgejo',
    value: Provider.GiteaForgejo,
  },
  {
    id: 'custom',
    icon: Settings,
    iconType: 'logo',
    label: 'Custom',
    value: Provider.Custom,
  },
];

type Props = {
  provider: Provider;
  authorizationType: AuthTypeOption;
  username: string;
  password: string;
  isEditing?: boolean;
  errors?: { username?: string; password?: string };
  onChange: (partial: {
    provider?: Provider;
    username?: string;
    password?: string;
    authorizationType?: AuthTypeOption;
  }) => void;
};

export function GitCredentialSection({
  provider,
  authorizationType,
  username,
  password,
  isEditing = false,
  errors,
  onChange,
}: Props) {
  const config = providerConfigs[provider];

  let { passwordLabel } = config;
  if (provider === Provider.Custom) {
    if (authorizationType === AuthTypeOption.Basic) {
      passwordLabel = 'Password';
    } else {
      passwordLabel = 'Access Token';
    }
  }

  return (
    <>
      <FormSection title="Authentication">
        <div className="form-group">
          <div className="col-sm-12 text-muted small">
            Select your Git provider for the best experience, or use Custom for
            other setups.
          </div>
        </div>
        <BoxSelector
          radioName="Provider"
          options={providerOptions}
          value={provider}
          onChange={(value) => {
            const providerConfig = providerConfigs[value];
            onChange({
              provider: value,
              authorizationType:
                providerConfig.fixedAuthType ?? authorizationType,
            });
          }}
        />
      </FormSection>

      <FormSection
        title={
          <div className="flex items-center gap-2">
            <div className="inline-flex h-14 w-14 items-center justify-center text-7xl">
              <Icon icon={config.icon} />
            </div>
            <div>{config.title}</div>
          </div>
        }
      >
        <div className="form-group">
          <div className="col-sm-12 text-muted small">{config.description}</div>
        </div>

        {provider === Provider.Custom && (
          <FormControl inputId="authorizationType" label="Authorization type">
            <RadioGroup
              options={authTypeOptions}
              selectedOption={authorizationType}
              onOptionChange={(value) => onChange({ authorizationType: value })}
              name="authorizationType"
            />
          </FormControl>
        )}

        <FormControl
          inputId="Username"
          label="Username"
          errors={errors?.username}
          tooltip={config.usernameTooltip}
        >
          <Input
            id="Username"
            name="username"
            value={username}
            autoComplete="off"
            onChange={(e) => onChange({ username: e.target.value })}
            data-cy="component-gitUsernameInput"
          />
        </FormControl>

        <FormControl
          inputId="Password"
          label={passwordLabel}
          errors={errors?.password}
          required={!isEditing}
          tooltip={config.passwordTooltip}
        >
          <Input
            id="Password"
            name="password"
            type="password"
            value={password}
            autoComplete="off"
            onChange={(e) => onChange({ password: e.target.value })}
            data-cy="component-gitPasswordInput"
          />
        </FormControl>
      </FormSection>
    </>
  );
}
